import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LightRays from "./LightRays";
import Header from "./Header";
import logo from "../assets/1.png";
import "./Login.css";

// 🧠 Utility functions
function sniffClient() {
  const ua = navigator.userAgent || "";
  let os = "Unknown";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Mac OS X/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";

  let browser = "Unknown";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";

  const device = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? "Mobile" : "Desktop";
  return { os, browser, device };
}

function getGeoPosition(timeoutMs = 3500) {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve({ latitude: "", longitude: "", city: "", country: "" });
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve({ latitude: "", longitude: "", city: "", country: "" });
      }
    }, timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({
          latitude: pos.coords.latitude?.toString() || "",
          longitude: pos.coords.longitude?.toString() || "",
          city: "",
          country: ""
        });
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ latitude: "", longitude: "", city: "", country: "" });
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 }
    );
  });
}

function getCurrentIndiaTime() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istOffset = 5.5 * 60 * 60000;
  const istTime = new Date(utc + istOffset);
  return istTime.toISOString();
}

// 🧩 Component
export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [clientInfo, setClientInfo] = useState({ device: "", os: "", browser: "" });
  const navigate = useNavigate();

  useEffect(() => setClientInfo(sniffClient()), []);

  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("employee");
      localStorage.removeItem("role");
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const { device, os, browser } = clientInfo.device ? clientInfo : sniffClient();
      const geo = await getGeoPosition();
      const loginTimeIST = getCurrentIndiaTime();

      const body = {
        username,
        password,
        device,
        os,
        browser,
        city: geo.city || "",
        country: geo.country || "",
        latitude: geo.latitude || "",
        longitude: geo.longitude || "",
        loginTimeIST,
      };

      const res = await fetch("https://hrms.urbango.co.in/server/index.php?endpoint=login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        setError("Server returned invalid response. Please try again.");
        return;
      }

      if (data?.success) {
        const employee = {
          username: data.username,
          employeeNo: data.employeeNo,
          name: data.employeeName,
          email: data.email,
          bloodGroup: data.bloodGroup,
          phoneNumber: data.phoneNumber,
          address: data.address,
        };
        localStorage.setItem("employee", JSON.stringify(employee));
        localStorage.setItem("role", "employee");
        navigate("/employee", { replace: true });
      } else {
        setError(data?.message || "Login failed. Please check your credentials.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Please try again.");
    }
  };

  return (
    <>
      {/* ✅ Fixed Header */}
      <Header />

      {/* ✅ Background with Light Rays */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          width: "100vw",
          height: "100vh",
          pointerEvents: "none",
          overflow: "hidden",
          background:
            "linear-gradient(120deg, rgba(110,180,255,0.24) 0%, rgba(218,136,255,0.14) 70%, rgba(255,255,255,0.12) 100%)",
        }}
      >
        <LightRays
          raysOrigin="top-center"
          raysColor="rgba(255, 17, 0, 0.45)"
          raysSpeed={1.5}
          lightSpread={0.8}
          rayLength={1.2}
          followMouse={true}
          mouseInfluence={0.1}
          noiseAmount={0.1}
          distortion={0.05}
          className="custom-rays"
        />
      </div>

      {/* ✅ Login Section */}
      <div className="lg-login-root">
        <div className="lg-login-shell" style={{ zIndex: 1 }}>
          {/* Left Info Panel */}
          <section className="lg-left glass hover-reflect">
            <div className="lg-left-inner">
              <h1 className="lg-hero-title">
                Performance <br /> Management
              </h1>
              <div className="lg-rotator-wrap">
                <div className="role-container">
                  <span className="role">Access Control</span>
                  <span className="role">Attendance</span>
                  <span className="role">Leave Request</span>
                  <span className="role">Employee Details</span>
                  <span className="role">Leave Balance</span>
                </div>
                <p className="lg-hero-sub">
                  Optimize performance reviews and goal-setting with our HRMS tools, fostering employee growth.
                </p>
              </div>
            </div>
          </section>

          {/* Right Login Box */}
          <section className="lg-right">
            <div className="login-form-wrapper glass hover-reflect">
              <img src={logo} alt="Urbango Logo" className="login-logo" />
              <h2 className="login-title">Employee Login</h2>

              <form onSubmit={handleSubmit} className="login-form">
                <input
                  className="login-input"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus
                />
                <div className="password-input-wrap">
                  <input
                    className="login-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                        <path
                          d="M1 12C3.5 6.5 8 3 12 3C16 3 20.5 6.5 23 12C20.5 17.5 16 21 12 21C8 21 3.5 17.5 1 12Z"
                          stroke="#444"
                          strokeWidth="1.7"
                        />
                        <circle cx="12" cy="12" r="3.5" stroke="#444" strokeWidth="1.7" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                        <path
                          d="M1 12C3.5 6.5 8 3 12 3C16 3 20.5 6.5 23 12C20.5 17.5 16 21 12 21C8 21 3.5 17.5 1 12Z"
                          stroke="#888"
                          strokeWidth="1.7"
                        />
                        <line
                          x1="3"
                          y1="21"
                          x2="21"
                          y2="3"
                          stroke="#888"
                          strokeWidth="1.4"
                        />
                      </svg>
                    )}
                  </button>
                </div>

                <button className="login-btn" type="submit">
                  Login
                </button>
              </form>

              {error && <p className="login-error">{error}</p>}
            </div>
          </section>
        </div>

        {/* ✅ Bottom-Right Button */}
        <button
          className="switch-to-employee"
          onClick={() => navigate("/admin-login")}
        >
          Go to Admin Login →
        </button>
      </div>
    </>
  );
}
